/**
 * What the pattern editor needs to know about the line it is scheduling against, supplied by whichever
 * host mounts the schedule card.
 *
 * A token rather than launcher arguments so `ScheduleEditorLauncher.open` stays the two-argument
 * shape the card owns, and so the host that already holds the line — the only place that knows its
 * effective-from, timezone, and the tenant's working week — provides it once instead of threading it
 * through the card.
 *
 * `today` is part of the context on purpose: the engine is deterministic on it, and a preview that
 * silently re-rendered at midnight would be untestable. Hosts supply the real local date; tests supply
 * a fixed one.
 */

import { InjectionToken } from '@angular/core';
import { ScheduleKind, ScheduleWeekday } from '@jms/model/scheduling';

/**
 * What the editor is authoring against: a live plan's task, or a reusable definition.
 *
 * Stated explicitly rather than inferred from absent data. Every template difference below — no
 * calendar anchors, no blackouts, a preview that is an example rather than a commitment — follows
 * from the SAME fact, and a host that happens to be missing an `effectiveFrom` or a holiday set is
 * not making that statement. Inferring would also make the live editor's behaviour depend on how
 * completely its host had loaded, which is exactly the kind of accident this input removes.
 */
export type ScheduleMode = 'live' | 'template';

export interface ScheduleEditorContext {
  taskName: string;
  /** Line anchor, YYYY-MM-DD. DTSTART for calendar rules and the after-service first-due default. */
  effectiveFrom: string;
  effectiveTo?: string;
  timezone: string;
  /** Injected current local date, YYYY-MM-DD. */
  today: string;
  workWeek: ScheduleWeekday[];
  holidays: string[];
  /**
   * The kinds this host allows, or absent for all of them.
   *
   * The allow-list rides on the context rather than on `ScheduleEditorLauncher.open` because it
   * is a property of the HOST, not of the click: whichever surface mounts the card knows whether it
   * is authoring a live task or a reusable definition, and it already provides this token once.
   * Threading it through the card would make every caller restate something the host already knows.
   *
   * A live surface omits both fields and every option renders — the default is deliberately "no
   * opinion", so nothing existing had to change to keep working.
   */
  allowedKinds?: readonly ScheduleKind[];
  /** The calendar endings this host allows, or absent for all. Templates omit 'onDate'. */
  allowedEndings?: readonly string[];
  /**
   * Which of the two things this editor is authoring. Defaults to 'live', so every existing live host
   * keeps its behaviour without restating it.
   *
   * Separate from `allowedKinds` on purpose: the allow-lists say which VALUES may be stored, and the
   * server enforces the same list. The mode says what the surrounding UI means — whether there is a
   * calendar to anchor to, blackouts to honour, and a real next visit to name — and none of that is a
   * value the payload carries.
   */
  mode?: ScheduleMode;
}

export const SCHEDULE_EDITOR_CONTEXT = new InjectionToken<() => ScheduleEditorContext>('SCHEDULE_EDITOR_CONTEXT');
